import { defineStore } from "pinia";
import { useSessionStorage, useLocalStorage } from "@vueuse/core";

const serializer = {
  read: (v) => {
    try {
      return v ? JSON.parse(v) : null;
    } catch (error) {
      return null;
    }
  },
  write: (v) => JSON.stringify(v),
};

export const RequestType = {
  Blw: "blw",
  Csw: "csw",
};

export const RequestDetailType = {
  Dispose: "dispose",
  Collect: "collect",
};

export const useRequestStore = defineStore({
  id: "request",
  state: () => ({
    requestType: useSessionStorage("requestType", null), // blw, csw
    requestDetailType: useSessionStorage("requestDetailType", null), // dispose, collect
    blwData: useSessionStorage("blwData", null, { serializer }),
    cswData: useSessionStorage("cswData", null, { serializer }),
    step: useSessionStorage("step", 1),
    disposeInfoMeesages: [],
    blwItemCart: useSessionStorage("blwItemCart", [], { serializer }),
    isChangeInfo: useSessionStorage("isChangeInfo", false),
    channel: useSessionStorage("channel", "zg_web"),
    isEdit: useSessionStorage("isEdit", false),
    recentAddress: useLocalStorage("recentAddress", null, { serializer }),
    showPaymentDialog: useLocalStorage("showPaymentDialog", false),
    // 대생폐 HWM 신청조회 세션 — 접수 플로우(blwData.hwmSession)와 분리해 라이프사이클이 섞이지 않게 한다.
    bulkyLookupSession: useSessionStorage("bulkyLookupSession", null, {
      serializer,
    }),
  }),
  actions: {
    setRequestType(requestType, RequestDetailType) {
      this.requestType = requestType;
      this.requestDetailType = RequestDetailType;
    },
    isBlw() {
      return this.requestType === RequestType.Blw;
    },
    isDispose() {
      return this.requestDetailType === RequestDetailType.Dispose;
    },
    // 대생폐 배출신고 = HWM 공개 API 흐름 (버려드림 collect는 기존 흐름 유지)
    isBlwHwmDispose() {
      return this.isBlw() && this.isDispose();
    },
    getTitle() {
      return this.requestType === RequestType.Blw
        ? "대형생활폐기물"
        : "공사장생활폐기물";
    },
    setStep(step) {
      this.step = step;
    },
    setChange(val) {
      this.isChangeInfo = val;
    },
    resetAllData() {
      this.requestType = null;
      this.requestDetailType = null;
      this.blwData = null;
      this.isEdit = false;
      this.cswData = null;
      this.isChangeInfo = false;
      this.blwItemCart = [];
    },
    resetAddress() {
      if (!this.blwData) return;

      // 특정 키 삭제
      const keysToRemove = [
        "address",
        "addressDetail",
        "addressMemo",
        "disposeLocationId",
        "registeredLocationId",
        "hwmAddressId",
        "bCode",
        "municipalityName",
      ];
      keysToRemove.forEach((key) => delete this.blwData[key]);

      // 새로운 객체로 업데이트해서 반응성을 유지
      this.blwData = { ...this.blwData };
    },
    resetDateData() {
      if (!this.blwData) return;
      delete this.blwData.disposeDate;
      this.blwData = { ...this.blwData };
    },
    resetDisposeItem() {
      if (!this.blwData) return;
      delete this.blwData.disposeList;
      this.blwData = { ...this.blwData };
    },
    resetDisposeUser() {
      if (!this.blwData) return;
    },
    resetRecentAddress() {
      this.recentAddress = null;
    },
    setDateData(date) {
      if (this.isBlw()) {
        this.blwData = {
          ...this.blwData,
          disposeDate: date,
        };
      } else {
        this.cswData = {
          ...this.cswData,
          disposeDate: date,
        };
      }
    },
    setAddressData(
      address,
      addressDetail = null,
      addressMemo = null,
      disposeLocationId,
      registeredLocationId,
      floorNum = null,
      isElevator = null,
    ) {
      const data = {};
      data.address = address;
      data.addressDetail = addressDetail;
      data.addressMemo = addressMemo;
      data.disposeLocationId = disposeLocationId;
      data.registeredLocationId = registeredLocationId;
      data.floorNum = floorNum;
      data.isElevator = isElevator;

      if (this.isBlw()) {
        this.blwData = {
          ...this.blwData,
          ...data,
        };
      } else {
        this.cswData = {
          ...this.cswData,
          ...data,
        };
      }
    },
    setCswHwmAddressId(hwmAddressId) {
      this.cswData = {
        ...this.cswData,
        hwmAddressId: hwmAddressId,
      };
    },
    // 대생폐 HWM 주소 확정 — address/select 응답의 id(정수)와 b_code를 보관한다.
    // 지자체(bCode 앞 5자리)가 바뀌면 품목·가격 체계가 달라지므로 장바구니를 비운다.
    setBlwHwmAddress({ addressId, bCode }) {
      const prevSigungu = (this.blwData?.bCode || "").slice(0, 5);
      const nextSigungu = (bCode || "").slice(0, 5);
      if (prevSigungu && nextSigungu && prevSigungu !== nextSigungu) {
        this.blwItemCart = [];
        if (this.blwData?.disposeList) {
          delete this.blwData.disposeList;
        }
      }
      this.blwData = {
        ...this.blwData,
        hwmAddressId: addressId,
        bCode: bCode,
      };
    },
    // 품목트리 응답의 service_municipality.name (표시용)
    setBlwMunicipality(name) {
      this.blwData = {
        ...this.blwData,
        municipalityName: name,
      };
    },
    // 대생폐 비회원 신청인 정보
    setBlwApplicant({ name, phone }) {
      this.blwData = {
        ...this.blwData,
        applicantName: name,
        applicantPhone: phone,
      };
    },
    // 대생폐 HWM 세션 토큰(30분·무소진) — expiresAt은 epoch(ms)라 새로고침 후에도 만료 판정 가능
    setBlwSession({ token, expiresIn, phone }) {
      this.blwData = {
        ...this.blwData,
        hwmSession: {
          token: token,
          phone: phone,
          expiresAt: Date.now() + expiresIn * 1000,
        },
      };
    },
    clearBlwSession() {
      if (!this.blwData) return;
      delete this.blwData.hwmSession;
      this.blwData = { ...this.blwData };
    },
    // 대생폐 HWM 신청조회 세션 토큰(30분·무소진) — expiresAt은 epoch(ms)라 새로고침 후에도 만료 판정 가능
    setLookupSession({ token, expiresIn, phone, applicantName }) {
      this.bulkyLookupSession = {
        token: token,
        phone: phone,
        applicantName: applicantName,
        expiresAt: Date.now() + expiresIn * 1000,
      };
    },
    clearLookupSession() {
      this.bulkyLookupSession = null;
    },
    // 조회 재진입 시 유효성 검증 — 세션 만료 60초 버퍼
    isLookupSessionValid() {
      const session = this.bulkyLookupSession;
      return !!session?.token && Date.now() < session.expiresAt - 60_000;
    },
    // 제출 직전 세션 유효성 검증 — 사진 업로드 등 소요시간 대비 60초 버퍼
    isBlwSessionValid() {
      const session = this.blwData?.hwmSession;
      return !!session?.token && Date.now() < session.expiresAt - 60_000;
    },
    setBlwItemCart(selectedList) {
      selectedList.map((element) => {
        const find = this.blwItemCart.find(
          (element2) => element.id === element2.id,
        );
        if (find == null) {
          this.blwItemCart.push(element);
        } else {
          this.blwItemCart.map((element2) => {
            if (element.id === element2.id) {
              element2.quantity += element.quantity;
            }
          });
        }
      });
    },
    setBlwItemCartQuantity(item, isPlus) {
      this.blwItemCart.forEach((element) => {
        if (item.id === element.id) {
          if (isPlus) {
            element.quantity += 1;
          } else {
            if (element.quantity > 1) {
              element.quantity -= 1;
            }
          }
        }
      });
    },
    setDisposeItem(item) {
      if (this.isBlw()) {
        this.blwData = {
          ...this.blwData,
          disposeList: item,
        };
      } else {
        this.cswData = {
          ...this.cswData,
          disposeList: item,
        };
      }
    },
    setGovermentId(govId) {
      this.blwData = {
        ...this.blwData,
        govId: govId,
      };
    },
    setBlwPaymentId({
      paymentId,
      bankAccount,
      paymentSecretKey,
      pgPaymentMethodId,
      paymentGovName,
    }) {
      this.blwData = {
        ...this.blwData,
        paymentId: paymentId,
        bankAccount: bankAccount,
        paymentSecretKey: paymentSecretKey,
        pgPaymentMethodId: pgPaymentMethodId,
        paymentGovName: paymentGovName,
      };
    },
    setPaymentInfo(method, price) {
      this.blwData = {
        ...this.blwData,
        paymentMethod: method,
        totalPrice: price,
      };
    },
    setDepositDate(date) {
      this.blwData = {
        ...this.blwData,
        depositDate: date,
      };
    },
    setCswWasteData({ wasteItem, wasteMethod, wasteWeight } = null) {
      if (wasteItem) {
        this.cswData["wasteItem"] = wasteItem;
      }

      if (wasteMethod) {
        this.cswData["wasteMethod"] = wasteMethod;
      }

      if (wasteWeight) {
        this.cswData["wasteWeight"] = wasteWeight;
      }
    },
    setDisposeUser(
      nameOrCompany,
      number,
      carNumber,
      companyType,
      collectionPlace,
      authId,
    ) {
      this.cswData = {
        ...this.cswData,
        nameOrCompany: nameOrCompany,
        phoneNumber: number,
        carNumber: carNumber,
        companyType: companyType,
        collectionPlace: collectionPlace,
        authId: authId,
      };
    },
    removeDuplicateAddress(address, addressDetail) {
      const recentAddresses = this.recentAddress || [];
      const index = recentAddresses.findIndex(
        (item) =>
          JSON.stringify(item.address.address_name) ===
            JSON.stringify(address.address_name) &&
          JSON.stringify(item.addressDetail) === JSON.stringify(addressDetail),
      );
      // 중복된 데이터가 있으면 삭제
      if (index !== -1) {
        recentAddresses.splice(index, 1);
      }
      return recentAddresses;
    },
    setRecentAddress(address, addressDetail) {
      const data = { address, addressDetail };
      const recentAddresses = this.removeDuplicateAddress(
        address,
        addressDetail,
      );
      recentAddresses.unshift(data);
      this.recentAddress = recentAddresses;
    },
    deleteRecentAddress(address, addressDetail) {
      const recentAddresses = this.removeDuplicateAddress(
        address,
        addressDetail,
      );
      this.recentAddress = recentAddresses;
    },
    deleteBlwItemCart(item) {
      const idx = this.blwItemCart.findIndex(
        (element) => element.id === item.id,
      );
      if (idx > -1) {
        this.blwItemCart.splice(idx, 1);
      }
    },
    getDisposeInfoMessages() {
      if (this.requestType === RequestType.Blw) {
        if (this.requestDetailType === RequestDetailType.Dispose) {
          return [
            "배출 일자는 30일 이내로만 선택이 가능합니다.",
            "지정하신 날짜에 배출해주세요.",
            "수거는 영업일 기준 2일이 소요되며 상황에 따라 지연될 수 있습니다.",
            "주말 및 공휴일에는 수거를 하지 않습니다.",
            "공동주택(아파트 등)의 경우 정해진 요일에 수거합니다.",
          ];
        } else {
          return [
            "배출 일자는 14일 이내로만 선택이 가능합니다.",
            "일요일 및 공휴일에는 수거를 하지 않습니다.",
            "배출신청(스티커)은 별도로 진행이 필요합니다.",
          ];
        }
      } else {
        return [
          "배출 일자는 14일 이내로만 선택이 가능합니다.",
          "지정하신 날짜에 배출해주세요.",
        ];
      }
    },
    getCompleteTitle() {
      return this.requestDetailType === RequestDetailType.Dispose
        ? "배출 신고"
        : this.requestType === RequestType.Blw
        ? "버려드림"
        : "배차신청";
    },
    getCompleteInfoTitle(paymentMethod = null) {
      if (this.requestDetailType === RequestDetailType.Dispose) {
        return this.requestType === RequestType.Blw
          ? paymentMethod === "card"
            ? "배출번호"
            : "입금정보 안내"
          : "확인사항 안내";
      }
      return this.requestType === RequestType.Blw
        ? "확인사항 안내"
        : "예상 비용 안내";
    },
    getTotalStep() {
      if (this.requestType === RequestType.Blw) {
        // 배출신고(HWM)는 배출자 인증 단계가 추가되어 5단계, 버려드림은 기존 4단계
        return this.isDispose() ? 5 : 4;
      } else {
        return 5;
      }
    },
    getNoticeTitle() {
      if (this.requestType === RequestType.Blw) {
        if (this.requestDetailType === RequestDetailType.Dispose) {
          return "배출 신청";
        } else {
          return "버려드림 서비스";
        }
      } else {
        return "배출";
      }
    },
    getNoticeContent() {
      if (this.requestType === RequestType.Blw) {
        if (this.requestDetailType === RequestDetailType.Dispose) {
          return "배출일 전까지 결제가 완료되어야 서비스가 진행됩니다.\
          (계좌이체 입금 확인은 영업시간(평일 09시 - 18시)내 처리 가능합니다.) \
          배출신고 폐기물은 반드시 지정된 장소에 배출해 주세요.";
        } else {
          return "버려드림 서비스는 전문기사가 방문하여 폐기물을 입력하신 배출 장소로 내려드리며, \
          각 품목의 스티커(배출신고) 구매는 별도로 진행이 필요합니다. \
          접수 후 전문 상담원이 3시간내로 빠르게 연락 드릴 예정입니다.";
        }
      } else {
        if (this.requestDetailType === RequestDetailType.Dispose) {
          return [
            "지구하다는 고객과 해당 지자체를 연결하는 플랫폼 서비스입니다.",
            "폐기물 배출 후 수거관련 문의는 해당 지자체로 문의하여 확인 가능합니다.",
          ];
        } else {
          return [
            "지구하다는 폐기물 수집·운반 대행 서비스를 제공합니다.",
            "폐기물 상차 후, 실제 배출량과 성상에 따라 제공된 예상 금액은 변동될 수 있습니다.",
            "서비스 환경에 따라 현장 비용이 추가로 발생할 수 있습니다. \
            (예시: 마대 작업, 사다리차 이용, 유료주차장 비용 등)",
            "방문수거 접수 후, 전문 담당자가 영업 시간 기준 3시간 내 연락드립니다.",
            "출발 후, 회차 진행 시 회차 비용은 50,000원이 발생합니다.",
            "빠른 배차가 필요한 경우 고객센터(1660-3114) 또는 요금표 > 전화문의로 요청 부탁드립니다.",
          ];
        }
      }
    },
    setShowPaymentAlert(value) {
      this.showPaymentDialog = value;
    },
    getShowPaymentAlert() {
      return this.showPaymentDialog;
    },
  },
});
